import { BadDataError, NotFoundError } from '@takeshape/errors';
import { createSchemaPropertyAccessor, followRef, getQueriesShape, getRef, isInterfaceRef, normalizeRefExpression, parseRef, refItemToShape, serializeRef } from '@takeshape/schema';
import { BREAK, getArgumentValues, getEnterLeaveForKind, Kind, parse, visit } from 'graphql';
import set from 'lodash/fp/set.js';
import get from 'lodash/get.js';
import uniq from 'lodash/uniq.js';
export function isOperationDefinitionNode(t) {
    return t.kind === Kind.OPERATION_DEFINITION;
}
export function isFragmentNode(t) {
    return t.kind === Kind.FRAGMENT_DEFINITION;
}
export function isFieldNode(t) {
    return t.kind === Kind.FIELD;
}
export function getOperation(doc) {
    const operationDefinition = doc.definitions.find(isOperationDefinitionNode);
    if (!operationDefinition) {
        throw new BadDataError('Unable to find first operation');
    }
    return operationDefinition;
}
export function getSelectionSetFromString(query) {
    const operationDefinition = getOperation(parse(query, { noLocation: true }));
    return operationDefinition.selectionSet;
}
export function getSelectionSetNamesFromString(query) {
    try {
        const op = getOperation(parse(query, { noLocation: true }));
        return getSelectionSetNames(op);
    }
    catch {
        return [];
    }
}
export function getSelectionSetPathsFromString(query) {
    const op = getOperation(parse(query, { noLocation: true }));
    return getSelectionSetPaths(op).map((path) => path.join('.'));
}
export function getFirstQueryName(doc) {
    const node = getOperation(doc).selectionSet.selections[0];
    if (node?.kind !== Kind.FIELD) {
        throw new BadDataError('Unable to find query name: operation is empty');
    }
    return node.name.value;
}
function hasName(node, name) {
    return ('name' in node && node.name.value === name) || ('alias' in node && node.alias?.value === name);
}
export function pathToOperationPath(path) {
    if (path.prev) {
        const result = pathToOperationPath(path.prev);
        return typeof path.key === 'string' ? result.concat(path.key) : result;
    }
    return typeof path.key === 'string' ? [path.key] : [];
}
export function followPath(selection, path, i = 0) {
    if (selection.selectionSet) {
        for (const node of selection.selectionSet.selections) {
            if (hasName(node, path[i])) {
                if (i === path.length - 1) {
                    return node;
                }
                return 'selectionSet' in node ? followPath(node, path, i + 1) : undefined;
            }
        }
    }
}
export function queryPathToAstPath(selection, path, i = 0, astPath = []) {
    if (selection.selectionSet) {
        for (let j = 0; j < selection.selectionSet.selections.length; j++) {
            const node = selection.selectionSet.selections[j];
            if (hasName(node, path[i])) {
                const newAstPath = [...astPath, 'selectionSet', 'selections', j];
                if (i === path.length - 1) {
                    return newAstPath;
                }
                return 'selectionSet' in node ? queryPathToAstPath(node, path, i + 1, newAstPath) : undefined;
            }
        }
    }
}
export function getSelectionSetPaths(node, pathSoFar = []) {
    let result = [];
    const selectionSet = node.kind === Kind.SELECTION_SET ? node : node.selectionSet;
    if (selectionSet) {
        for (const selection of selectionSet.selections) {
            if ('name' in selection && selection.name.value) {
                const path = [...pathSoFar, selection.name?.value];
                if ('selectionSet' in selection && selection.selectionSet) {
                    result = [...result, ...getSelectionSetPaths(selection, path)];
                }
                else {
                    result.push(path);
                }
            }
        }
    }
    return uniq(result);
}
export function getSelectionSetNames(node, fragments = {}) {
    const result = [];
    const selectionSet = node.kind === Kind.SELECTION_SET ? node : node.selectionSet;
    if (selectionSet) {
        for (const selection of selectionSet.selections) {
            if (selection.kind === Kind.FRAGMENT_SPREAD) {
                const fragment = fragments[selection.name.value];
                if (fragment) {
                    result.push(...getSelectionSetNames(fragment, fragments));
                }
            }
            else if (selection.kind === Kind.INLINE_FRAGMENT) {
                result.push(...getSelectionSetNames(selection));
            }
            else if ('name' in selection && selection.name.value) {
                result.push(selection.name.value);
            }
        }
    }
    return uniq(result);
}
export function getParentSelectionNames(op, path) {
    const opPath = pathToOperationPath(path);
    const parentOpPath = opPath.slice(0, opPath.length - 1);
    const node = followPath(op, parentOpPath);
    return node ? getSelectionSetNames(node) : [];
}
function isNode(maybeNode) {
    return Boolean(maybeNode && typeof maybeNode.kind === 'string');
}
export function createSchemaInfoVisitor(projectSchema, initialShapeName) {
    let path = [];
    const parentShapeStack = [];
    const propSchemaStack = [];
    const createShapePropertyAccessor = createSchemaPropertyAccessor(projectSchema);
    if (initialShapeName) {
        const initialShape = projectSchema.shapes[initialShapeName];
        if (!initialShape) {
            throw new Error(`Shape "${initialShapeName}" not found`);
        }
        path = ['shapes', initialShapeName];
        parentShapeStack.push(initialShape);
        propSchemaStack.push({ '@ref': normalizeRefExpression(projectSchema, initialShapeName) });
    }
    return {
        enter(node) {
            if (node.kind === Kind.OPERATION_DEFINITION) {
                if (node.operation === 'query' || node.operation === 'mutation') {
                    path = [node.operation === 'query' ? 'queries' : 'mutations'];
                    parentShapeStack.push(getQueriesShape(projectSchema, node.operation));
                }
                else {
                    throw new Error('Subscriptions are not supported');
                }
            }
            else if (node.kind === Kind.INLINE_FRAGMENT || node.kind === Kind.FRAGMENT_DEFINITION) {
                const fragmentShapeName = node.typeCondition?.name?.value;
                propSchemaStack.push(fragmentShapeName ? { '@ref': normalizeRefExpression(projectSchema, fragmentShapeName) } : undefined);
            }
            else if (node.kind === Kind.SELECTION_SET) {
                const next = propSchemaStack[propSchemaStack.length - 1];
                let nextParent = parentShapeStack[parentShapeStack.length - 1];
                if (next) {
                    const ref = getRef(projectSchema, next.items ?? next);
                    if (ref) {
                        const shape = refItemToShape(projectSchema, ref);
                        if (shape) {
                            nextParent = shape;
                        }
                        else {
                            throw new NotFoundError(`Shape not found: ${serializeRef(ref)}`);
                        }
                    }
                }
                parentShapeStack.push(nextParent);
            }
            else if (node.kind === Kind.FIELD) {
                if (parentShapeStack.length) {
                    const shape = parentShapeStack[parentShapeStack.length - 1];
                    const schemaPropertyAccessor = createShapePropertyAccessor(shape);
                    path = ['shapes', shape.name, 'schema', 'properties', node.name.value];
                    const propertySchema = schemaPropertyAccessor.getValue(node.name.value);
                    propSchemaStack.push(propertySchema);
                }
            }
        },
        leave(node) {
            switch (node.kind) {
                case Kind.SELECTION_SET:
                case Kind.OPERATION_DEFINITION:
                    parentShapeStack.pop();
                    break;
                case Kind.INLINE_FRAGMENT:
                case Kind.FRAGMENT_DEFINITION:
                case Kind.FIELD:
                    propSchemaStack.pop();
                    break;
                default:
            }
        },
        getInfo() {
            return {
                projectSchema,
                path,
                propertySchema: propSchemaStack[propSchemaStack.length - 1],
                shape: parentShapeStack[parentShapeStack.length - 1]
            };
        }
    };
}
export function visitWithSchemaInfo(schemaInfoVisitor, visitor) {
    return {
        enter(...args) {
            const node = args[0];
            schemaInfoVisitor.enter(node);
            const fn = getEnterLeaveForKind(visitor, node.kind).enter;
            if (fn) {
                const result = fn.apply(visitor, args);
                if (result !== undefined) {
                    schemaInfoVisitor.leave(node);
                    if (isNode(result)) {
                        schemaInfoVisitor.enter(result);
                    }
                }
                return result;
            }
        },
        leave(...args) {
            const node = args[0];
            const fn = getEnterLeaveForKind(visitor, node.kind).leave;
            let result;
            if (fn) {
                result = fn.apply(visitor, args);
            }
            schemaInfoVisitor.leave(node);
            return result;
        }
    };
}
const setTypeConditionName = set('typeCondition.name.value');
export function removeNamespaceFromFragment(projectSchema) {
    return (node) => {
        const typeConditionName = node.typeCondition?.name?.value;
        return typeConditionName ? setTypeConditionName(parseRef(projectSchema, typeConditionName).shapeName, node) : node;
    };
}
/**
 * Unwrap type removing list and non-null wrappers
 * Example: [Foo!] -> Foo
 */
export function unwrapType(type) {
    return 'ofType' in type ? unwrapType(type.ofType) : type;
}
/**
 * Construct logic for ensuring ID selections fields in an ASTVisitor.
 */
export function createEnsureIds(projectSchema, schemaInfoVisitor, options) {
    const { idField, shapeId = false, typename = 'unions' } = options ?? {};
    return (node) => {
        const { propertySchema } = schemaInfoVisitor.getInfo();
        if (propertySchema) {
            const schema = propertySchema.items ?? propertySchema;
            const resolved = followRef(projectSchema, schema);
            let update;
            // ensure id
            if (idField && resolved.properties?.[idField]) {
                update = ensureScalarField(node, idField);
            }
            // ensure _shapeId
            if (shapeId && resolved.properties?._shapeId?.type === 'string') {
                update = ensureScalarField(update ?? node, '_shapeId');
            }
            // ensure __typename for unions and interfaces
            if (typename === 'all' || isInterfaceRef(projectSchema, schema) || resolved.oneOf) {
                update = ensureScalarField(update ?? node, '__typename');
            }
            return update;
        }
    };
}
/**
 * Construct and return an ASTVisitor for ensuring several ID selection fields.
 */
export function ensureIds(projectSchema, schemaInfoVisitor, options) {
    const ensure = createEnsureIds(projectSchema, schemaInfoVisitor, options);
    return {
        SelectionSet: {
            enter(...args) {
                return ensure(...args);
            }
        }
    };
}
export const PARENT_FRAGMENT = '_parentFragment';
export function hasParentFragment(node) {
    const path = [];
    let result = false;
    visit(node, {
        Field: {
            enter(node) {
                path.push(node.alias ? node.alias.value : node.name.value);
            },
            leave() {
                path.pop();
            }
        },
        FragmentSpread: {
            enter(node) {
                if (node.name.value === PARENT_FRAGMENT) {
                    result = true;
                    return BREAK;
                }
            }
        }
    });
    return result ? path : undefined;
}
export const createFragmentPredicate = (name) => (selection) => selection.kind === Kind.FRAGMENT_SPREAD && selection.name.value === name;
const parentFragmentPredicate = createFragmentPredicate(PARENT_FRAGMENT);
function parentFragmentNotPredicate(selection) {
    return !parentFragmentPredicate(selection);
}
export function inlineParentFragment(node, parentSelectionSet) {
    return visit(node, {
        SelectionSet(node) {
            for (const selection of node.selections) {
                if (parentFragmentPredicate(selection)) {
                    return {
                        ...node,
                        selections: [...parentSelectionSet.selections, ...node.selections.filter(parentFragmentNotPredicate)]
                    };
                }
            }
        }
    });
}
function createField(name) {
    return {
        kind: Kind.FIELD,
        name: {
            kind: Kind.NAME,
            value: name
        }
    };
}
/**
 * If the selection set is missing the specified scalar field add it, otherwise return undefined to signal no change
 */
export function ensureScalarField(node, name) {
    if (!node.selections.find((selection) => selection.kind === Kind.FIELD && selection.name.value === name)) {
        return {
            ...node,
            selections: [createField(name), ...node.selections]
        };
    }
}
export function wrapField(path, leafNode) {
    if (path.length === 0) {
        return leafNode;
    }
    const [first, ...rest] = path;
    return {
        kind: Kind.FIELD,
        name: {
            kind: Kind.NAME,
            value: first
        },
        selectionSet: {
            kind: Kind.SELECTION_SET,
            selections: [wrapField(rest, leafNode)]
        }
    };
}
export function getUsedVariables(node) {
    const varsUsed = new Set();
    const nodes = Array.isArray(node) ? node : [node];
    for (const node of nodes) {
        visit(node, {
            Variable: {
                enter(node) {
                    varsUsed.add(node.name.value);
                }
            }
        });
    }
    return varsUsed;
}
export function makeArrayItemsRequired(node) {
    if (node.type.kind !== Kind.NON_NULL_TYPE) {
        return { ...node, type: { kind: Kind.NON_NULL_TYPE, type: node.type } };
    }
    return node;
}
export function removeNamespaceVarDefinition({ projectSchema, layerSchema }) {
    const transform = (node) => {
        if (node.kind === Kind.LIST_TYPE) {
            // TODO fix hack sparse input is rare but should be allowed https://app.shortcut.com/takeshape/story/12250/better-sparse-array-type-support
            return { ...node, type: transform(makeArrayItemsRequired(node).type) };
        }
        if (node.kind === Kind.NON_NULL_TYPE) {
            return { ...node, type: transform(node.type) };
        }
        const name = node.name.value;
        let rawName;
        // TODO fix hack we need to account for adding Input suffixes better https://app.shortcut.com/takeshape/story/12254/improve-input-suffix-handling
        if (layerSchema && name.endsWith('Input') && !layerSchema.shapes[name]) {
            const combinedSchema = { ...projectSchema, ...layerSchema };
            rawName = parseRef(combinedSchema, name.substring(0, name.length - 5)).shapeName;
        }
        else {
            rawName = parseRef(projectSchema, name).shapeName;
        }
        return { ...node, name: { kind: Kind.NAME, value: rawName } };
    };
    return (node) => ({
        ...node,
        type: transform(node.type)
    });
}
export function transformVariableDefinitions(context, op, varsUsed) {
    return op.variableDefinitions
        ?.filter((node) => varsUsed.has(node.variable.name.value))
        .map(removeNamespaceVarDefinition(context));
}
function getFieldId(node) {
    return node.alias?.value ?? node.name.value;
}
export function shallowMergeSelectionSets(a, b) {
    const ids = new Set();
    for (const selection of b.selections) {
        if (selection.kind === Kind.FIELD) {
            ids.add(getFieldId(selection));
        }
    }
    const mergedSelections = [
        ...a.selections.filter((selection) => selection.kind !== Kind.FIELD || !ids.has(getFieldId(selection))),
        ...b.selections
    ];
    return {
        kind: Kind.SELECTION_SET,
        selections: mergedSelections
    };
}
/**
 * Update a query document at a given path
 * @param basePath The query path of a selection to filter
 * @param fn the update function which returns a new node or undefined to skip the update
 * @param doc The source document
 * @return A new document with the filtered selection
 */
export function updateDocumentIn(basePath, fn, doc) {
    const i = doc.definitions.findIndex(isOperationDefinitionNode);
    const operation = getOperation(doc);
    const pathToNode = queryPathToAstPath(operation, basePath);
    if (pathToNode) {
        const astPath = ['definitions', i, ...pathToNode];
        const node = get(doc, astPath);
        if (isNode(node)) {
            const updated = fn(node);
            if (updated !== undefined) {
                return set(astPath, updated, doc);
            }
        }
    }
    return doc;
}
/**
 * Filter the selection at the given path within a document.
 * @param basePath The query path of a selection to filter
 * @param predicate The filter predicate
 * @param doc The source document
 * @return A new document with the filtered selection
 */
export function filterDocument(basePath, predicate, doc) {
    return updateDocumentIn(basePath, (node) => {
        if ('selectionSet' in node && node.selectionSet) {
            return {
                ...node,
                selectionSet: {
                    ...node.selectionSet,
                    selections: node.selectionSet.selections.filter(predicate)
                }
            };
        }
    }, doc);
}
export function setSelectionSet(doc, pathToSelectionSet, selectionSet, shouldMerge) {
    return updateDocumentIn(pathToSelectionSet, (node) => {
        const nodeHasSelectionSet = 'selectionSet' in node && node.selectionSet;
        return {
            ...node,
            selectionSet: nodeHasSelectionSet && shouldMerge ? shallowMergeSelectionSets(node.selectionSet, selectionSet) : selectionSet
        };
    }, doc);
}
export function getFragmentsUsed(doc) {
    const fragmentsUsed = new Set();
    visit(doc, {
        FragmentSpread: {
            enter(node) {
                fragmentsUsed.add(node.name.value);
            }
        }
    });
    return doc.definitions.filter(isFragmentNode).filter((def) => fragmentsUsed.has(def.name.value));
}
export function appendDefinitions(doc, definitions) {
    if (definitions.length) {
        return { ...doc, definitions: [...doc.definitions, ...definitions] };
    }
    return doc;
}
function getSchemaDirectiveMap(schema) {
    const schemaDirectives = schema.getDirectives();
    const schemaDirectiveMap = schemaDirectives.reduce((directiveMap, schemaDirective) => {
        directiveMap[schemaDirective.name] = schemaDirective;
        return directiveMap;
    }, {});
    return schemaDirectiveMap;
}
function getDirectivesFromInfo(info) {
    const schemaDirectiveMap = getSchemaDirectiveMap(info.schema);
    const result = [];
    for (const astNode of info.fieldNodes) {
        if (astNode.directives) {
            for (const directiveNode of astNode.directives) {
                const schemaDirective = schemaDirectiveMap[directiveNode.name.value];
                if (schemaDirective) {
                    result.push({
                        name: directiveNode.name.value,
                        args: getArgumentValues(schemaDirective, directiveNode)
                    });
                }
            }
        }
    }
    return result;
}
export function getDirectiveFromInfo(info, directiveName) {
    return getDirectivesFromInfo(info).find((directive) => directive.name === directiveName);
}
/**
 * Creates a variable definition AST node with the given name and type.
 */
export function createVariableDefinition(varName, typeName) {
    return {
        kind: Kind.VARIABLE_DEFINITION,
        variable: {
            kind: Kind.VARIABLE,
            name: { kind: Kind.NAME, value: varName }
        },
        type: {
            kind: Kind.NAMED_TYPE,
            name: { kind: Kind.NAME, value: typeName }
        }
    };
}
/**
 * Get the set of variable names that are declared in an operation definition.
 */
export function getDeclaredVariables(operation) {
    const declared = new Set();
    if (operation.variableDefinitions) {
        for (const def of operation.variableDefinitions) {
            declared.add(def.variable.name.value);
        }
    }
    return declared;
}
/**
 * Find variables that are used in the document but not declared in the operation.
 */
export function getUndeclaredVariables(doc) {
    const operation = getOperation(doc);
    const declared = getDeclaredVariables(operation);
    const used = getUsedVariables(doc);
    const undeclared = new Set();
    for (const varName of used) {
        if (!declared.has(varName)) {
            undeclared.add(varName);
        }
    }
    return undeclared;
}
/**
 * Add variable definitions to a document for variables that are used but not declared.
 * Only adds definitions for variables that appear in the provided typeMapping.
 */
export function addVariableDefinitions(doc, typeMapping) {
    const undeclared = getUndeclaredVariables(doc);
    // Filter to only variables we have type info for and that are undeclared
    const toAdd = typeMapping.filter((mapping) => undeclared.has(mapping.name));
    if (toAdd.length === 0) {
        return doc;
    }
    return visit(doc, {
        OperationDefinition(node) {
            const newVarDefs = toAdd.map((mapping) => createVariableDefinition(mapping.name, mapping.type));
            return {
                ...node,
                variableDefinitions: [...(node.variableDefinitions ?? []), ...newVarDefs]
            };
        }
    });
}
/**
 * Create a nested field node from a path array.
 * e.g., ["pageInfo", "endCursor"] becomes `pageInfo { endCursor }`
 */
function createNestedFieldNode(pathParts) {
    if (pathParts.length === 0) {
        throw new Error('Cannot create field node from empty path');
    }
    if (pathParts.length === 1) {
        return {
            kind: Kind.FIELD,
            name: { kind: Kind.NAME, value: pathParts[0] }
        };
    }
    const [first, ...rest] = pathParts;
    return {
        kind: Kind.FIELD,
        name: { kind: Kind.NAME, value: first },
        selectionSet: {
            kind: Kind.SELECTION_SET,
            selections: [createNestedFieldNode(rest)]
        }
    };
}
/**
 * Check if a field path exists in a selection set.
 */
function hasFieldPath(selectionSet, pathParts) {
    if (!selectionSet || pathParts.length === 0) {
        return pathParts.length === 0;
    }
    const [first, ...rest] = pathParts;
    for (const selection of selectionSet.selections) {
        if (selection.kind === Kind.FIELD && selection.name.value === first) {
            if (rest.length === 0) {
                return true;
            }
            return hasFieldPath(selection.selectionSet, rest);
        }
    }
    return false;
}
/**
 * Add a field path to a selection set, creating nested structure as needed.
 * Returns a new selection set with the field added.
 */
function addFieldPathToSelectionSet(selectionSet, pathParts) {
    if (pathParts.length === 0) {
        return selectionSet;
    }
    const [first, ...rest] = pathParts;
    // Check if the first field already exists
    const existingFieldIndex = selectionSet.selections.findIndex((s) => s.kind === Kind.FIELD && s.name.value === first);
    if (existingFieldIndex >= 0) {
        // Field exists, recurse into it if there are more path parts
        if (rest.length === 0) {
            return selectionSet; // Already have this leaf field
        }
        const existingField = selectionSet.selections[existingFieldIndex];
        const updatedField = {
            ...existingField,
            selectionSet: addFieldPathToSelectionSet(existingField.selectionSet ?? { kind: Kind.SELECTION_SET, selections: [] }, rest)
        };
        return {
            ...selectionSet,
            selections: [
                ...selectionSet.selections.slice(0, existingFieldIndex),
                updatedField,
                ...selectionSet.selections.slice(existingFieldIndex + 1)
            ]
        };
    }
    // Field doesn't exist, create the entire nested structure
    return {
        ...selectionSet,
        selections: [...selectionSet.selections, createNestedFieldNode(pathParts)]
    };
}
/**
 * Ensure that specific field paths exist in the selection set at a given location.
 * This is used to ensure pagination metadata fields (like pageInfo.endCursor) are present.
 *
 * @param doc The document to modify
 * @param basePath Path to the field containing the selection set (e.g., ['variants'] for dataPath)
 * @param fieldPaths Field paths to ensure exist (e.g., [['pageInfo', 'endCursor'], ['pageInfo', 'hasNextPage']])
 * @returns Modified document with required fields added
 */
export function ensureSelectionSetFields(doc, basePath, fieldPaths) {
    if (fieldPaths.length === 0) {
        return doc;
    }
    const operation = getOperation(doc);
    const rootField = operation.selectionSet.selections[0];
    if (!rootField || rootField.kind !== Kind.FIELD) {
        return doc;
    }
    // Build full path: rootField + basePath
    const fullPath = [rootField.name.value, ...basePath];
    return updateDocumentIn(fullPath, (node) => {
        if (node.kind !== Kind.FIELD || !node.selectionSet) {
            return node;
        }
        // Filter to only field paths that don't already exist
        const missingPaths = fieldPaths.filter((pathParts) => !hasFieldPath(node.selectionSet, pathParts));
        if (missingPaths.length === 0) {
            return node;
        }
        // Add each missing path to the selection set
        let updatedSelectionSet = node.selectionSet;
        for (const pathParts of missingPaths) {
            updatedSelectionSet = addFieldPathToSelectionSet(updatedSelectionSet, pathParts);
        }
        return {
            ...node,
            selectionSet: updatedSelectionSet
        };
    }, doc);
}
/**
 * Add arguments with variable references to a field at a given path in the document.
 * For example, given path ['variants'] and arguments [{ name: 'first', variableName: 'first' }],
 * transforms `variants { ... }` to `variants(first: $first) { ... }`
 *
 * @param doc The document to modify
 * @param fieldPath Path to the field (e.g., ['variants'] for a nested field)
 * @param args Arguments to add as variable references
 * @returns Modified document with arguments added to the field
 */
export function addFieldArguments(doc, fieldPath, args) {
    if (args.length === 0 || fieldPath.length === 0) {
        return doc;
    }
    const operation = getOperation(doc);
    // The full path from the operation includes the root query field
    const rootField = operation.selectionSet.selections[0];
    if (!rootField || rootField.kind !== Kind.FIELD) {
        return doc;
    }
    // Build full path: rootField + fieldPath
    const fullPath = [rootField.name.value, ...fieldPath];
    return updateDocumentIn(fullPath, (node) => {
        if (node.kind !== Kind.FIELD) {
            return node;
        }
        // Get existing arguments
        const existingArgs = node.arguments ?? [];
        const existingArgNames = new Set(existingArgs.map((arg) => arg.name.value));
        // Create new argument nodes for args that don't already exist
        const newArgs = args
            .filter((arg) => !existingArgNames.has(arg.name))
            .map((arg) => ({
            kind: Kind.ARGUMENT,
            name: { kind: Kind.NAME, value: arg.name },
            value: {
                kind: Kind.VARIABLE,
                name: { kind: Kind.NAME, value: arg.variableName }
            }
        }));
        if (newArgs.length === 0) {
            return node;
        }
        return {
            ...node,
            arguments: [...existingArgs, ...newArgs]
        };
    }, doc);
}
